// The tensor app's sync engine (design 02 8.2, the same way as talk's
// src/state/engine.ts, with its own API and socket):
//   1. GET /api/tensor/bootstrap -> me, family, workbooks, cursor
//   2. open /ws/tensor; changes that arrive are buffered
//   3. on "hello": GET /api/tensor/sync?since=cursor until done, then apply
//      the buffer (changes with id <= cursor are skipped)
//   4. live: apply each "change" as it comes
// A workbook's items, budgets and rows are read when it is opened (pages of
// 5000 rows); changes that come meanwhile are kept by version.
// The socket sends a ping every 25 s; without a pong in 10 s it is closed
// and opened again with a growing delay (max 30 s).
//
// Changes of rows are shown at once and sent in order (design 02 4.4). When
// there is no connection they wait (kept in this browser) and go again
// when it is back. The last write wins; when someone else had changed the
// row after the version the screen showed, the server says stale and the
// app tells the person.
import { ApiError, NetworkError, api, errorText } from '../api/client'
import { loginWithPasskey } from '../features/passkeys/passkeys'
import { newClientId } from '../lib/ids'
import { showToast } from '../ui/toast'
import {
  type BuiltinRole,
  type Id,
  type TensorChange,
  type TensorDef,
  type TensorDefInput,
  type TensorDim,
  type TensorDimInput,
  type TensorDimKey,
  type TensorEntryInput,
  type TensorSavedView,
  type TensorViewConfig,
  type TensorRow,
  type TensorWsMessage,
  type Visibility,
  tapi,
} from './api'
import * as M from './model'
import { getState, setState, subscribe } from './store'

const PING_INTERVAL_MS = 25_000
const PONG_TIMEOUT_MS = 10_000
const MAX_BACKOFF_MS = 30_000
const OPS_RETRY_MIN_MS = 3_000
const OPS_RETRY_MAX_MS = 30_000
const PAGE = 5000

const PREF_WORKBOOK = 'tensor.workbook'
const opsKey = (me: Id) => `tensor.ops.${me}`
const paymentKey = (workbookId: Id) => `tensor.payment.${workbookId}`

export const STALE_TEXT = '다른 사람이 먼저 고쳤어요 — 내 값으로 저장했어요'

type Timer = ReturnType<typeof setTimeout>

/** Who else wants the changes as they are applied (the 표 screen's grid,
    grid/store.ts): the changes in order, or 'reset' when everything was
    read again. */
export type ChangeListener = (changes: TensorChange[] | 'reset') => void
const changeListeners = new Set<ChangeListener>()

export function onTensorChanges(l: ChangeListener): () => void {
  changeListeners.add(l)
  return () => changeListeners.delete(l)
}

function tellListeners(changes: TensorChange[] | 'reset'): void {
  for (const l of changeListeners) {
    try {
      l(changes)
    } catch (e) {
      console.error(e)
    }
  }
}

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // only a convenience
  }
}

/** The workbook opened last on this device. */
export function lastWorkbook(): Id {
  return Number(readLocal(PREF_WORKBOOK)) || 0
}

/** The payment item I used last in this workbook on this device (0: none). */
export function lastPayment(workbookId: Id): Id {
  return Number(readLocal(paymentKey(workbookId))) || 0
}

class Engine {
  private ws: WebSocket | null = null
  private helloSeen = false
  private backoff = 1000
  private reconnectTimer: Timer | undefined
  private bootstrapTimer: Timer | undefined
  private pingTimer: ReturnType<typeof setInterval> | undefined
  private pongTimer: Timer | undefined
  private syncing = false
  private buffer: TensorChange[] = []
  private started = false
  private loads = new Map<Id, Promise<void>>()
  private opsBusy = false
  private opsRetry: Timer | undefined
  private opsDelay = OPS_RETRY_MIN_MS
  private keptOps: M.RowOp[] | null = null

  start(): void {
    if (this.started) return
    this.started = true
    window.addEventListener('online', () => {
      this.reconnectNow()
      this.flushOps()
    })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.reconnectNow()
        this.flushOps()
      }
    })
    subscribe(() => this.keepOps())
    void this.bootstrap()
  }

  /** Loads everything again (start-up, after login, after a reset). */
  async bootstrap(): Promise<void> {
    clearTimeout(this.bootstrapTimer)
    try {
      const b = await tapi.bootstrap()
      const kept = this.loadOps(b.me.id)
      setState((s) => {
        const next = M.fromBootstrap(s, b)
        const ops = [...next.ops]
        for (const o of kept)
          if (next.workbooks[o.workbookId] && !ops.some((x) => x.key === o.key)) ops.push({ ...o, status: 'waiting' })
        return { ...next, ops }
      })
      this.connect()
      const cur = getState().current
      if (cur) void this.loadBook(cur)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        this.signedOut(null)
        return
      }
      setState((s) => ({ ...s, connection: 'offline' }))
      this.bootstrapTimer = setTimeout(() => void this.bootstrap(), this.nextDelay())
    }
  }

  private nextDelay(): number {
    const d = this.backoff + Math.random() * 500
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS)
    return d
  }

  // ---- WebSocket ----

  private connect(): void {
    if (this.ws || getState().phase !== 'ready') return
    clearTimeout(this.reconnectTimer)
    setState((s) => (s.connection === 'connecting' ? s : { ...s, connection: 'connecting' }))
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
    const ws = new WebSocket(`${proto}//${location.host}/ws/tensor`)
    this.ws = ws
    this.helloSeen = false
    this.syncing = true
    this.buffer = []
    ws.onmessage = (ev) => this.onMessage(ws, ev.data)
    ws.onclose = () => this.onClose(ws)
  }

  private onMessage(ws: WebSocket, data: unknown): void {
    if (ws !== this.ws || typeof data !== 'string') return
    let msg: TensorWsMessage
    try {
      msg = JSON.parse(data) as TensorWsMessage
    } catch {
      return
    }
    switch (msg.type) {
      case 'hello':
        this.helloSeen = true
        this.backoff = 1000
        this.startPing()
        void this.catchUp()
        break
      case 'change':
        if (this.syncing) this.buffer.push(msg.change)
        else this.apply([msg.change])
        break
      case 'pong':
        clearTimeout(this.pongTimer)
        break
      case 'resync':
        this.syncing = true
        void this.catchUp()
        break
      case 'bye':
        if (msg.reason === 'session_revoked') this.signedOut('이 기기는 로그아웃되었습니다.')
        break
    }
  }

  private onClose(ws: WebSocket): void {
    if (ws !== this.ws) return
    this.ws = null
    clearInterval(this.pingTimer)
    clearTimeout(this.pongTimer)
    if (getState().phase !== 'ready') return
    setState((s) => ({ ...s, connection: navigator.onLine === false ? 'offline' : 'connecting' }))
    if (!this.helloSeen) void this.checkSession()
    this.reconnectTimer = setTimeout(() => this.connect(), this.nextDelay())
  }

  /** A socket that closes before "hello" may mean the session is gone. */
  private async checkSession(): Promise<void> {
    try {
      await tapi.sync(getState().cursor, 1)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) this.signedOut('다시 로그인해 주세요.')
    }
  }

  private startPing(): void {
    clearInterval(this.pingTimer)
    this.pingTimer = setInterval(() => this.ping(), PING_INTERVAL_MS)
  }

  private ping(): void {
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'ping', t: Date.now() }))
    clearTimeout(this.pongTimer)
    this.pongTimer = setTimeout(() => ws.close(), PONG_TIMEOUT_MS)
  }

  reconnectNow(): void {
    if (getState().phase !== 'ready') return
    if (this.ws) {
      // a socket that looks open may be dead after sleep: ping it now
      if (this.ws.readyState === WebSocket.OPEN) this.ping()
      return
    }
    this.backoff = 1000
    this.connect()
  }

  // ---- catching up ----

  private async catchUp(): Promise<void> {
    this.syncing = true
    const ws = this.ws
    try {
      for (;;) {
        const r = await tapi.sync(getState().cursor)
        if (r.reset) {
          // the change log was pruned: read everything again
          const b = await tapi.bootstrap()
          setState((s) => M.fromBootstrap(s, b))
          tellListeners('reset')
          const cur = getState().current
          if (cur) void this.loadBook(cur)
          break
        }
        this.apply(r.changes)
        setState((s) => (r.cursor > s.cursor ? { ...s, cursor: r.cursor } : s))
        if (!r.has_more) break
      }
      if (ws !== this.ws) return
      const cursor = getState().cursor
      const buffered = this.buffer.filter((c) => c.id > cursor)
      this.buffer = []
      this.syncing = false
      this.apply(buffered)
      setState((s) => ({ ...s, connection: 'online' }))
      this.flushOps()
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        this.signedOut('다시 로그인해 주세요.')
        return
      }
      ws?.close() // try the whole thing again
    }
  }

  private apply(changes: TensorChange[]): void {
    if (changes.length === 0) return
    setState((s) => {
      let next = s
      for (const c of changes) next = M.applyChange(next, c)
      const last = changes[changes.length - 1]!.id
      return last > next.cursor ? { ...next, cursor: last } : next
    })
    tellListeners(changes)
  }

  // ---- workbooks ----

  /** Shows a workbook (and reads it when it is not read yet). */
  open(id: Id): void {
    if (!getState().workbooks[id]) return
    writeLocal(PREF_WORKBOOK, String(id))
    setState((s) => (s.current === id ? s : { ...s, current: id }))
    if (!getState().books[id]) void this.loadBook(id)
  }

  /** Reads a workbook: items and budgets, then its rows by pages. */
  loadBook(id: Id): Promise<void> {
    const p = this.loads.get(id)
    if (p) return p
    const np = this.readBook(id).finally(() => this.loads.delete(id))
    this.loads.set(id, np)
    return np
  }

  private async readBook(id: Id): Promise<void> {
    // changes that come while reading are kept on this empty book
    setState((s) =>
      s.books[id] ? s : { ...s, books: { ...s.books, [id]: M.emptyBook() } },
    )
    try {
      const full = await tapi.workbook(id)
      setState((s) => {
        const next = M.putWorkbook(s, full.workbook)
        return { ...next, books: { ...next.books, [id]: M.bookFromFull(full, next.books[id]) } }
      })
      let after = 0
      for (;;) {
        if (!getState().books[id]) return // removed meanwhile
        const page = await tapi.rows(id, after, PAGE)
        setState((s) => M.withBook(s, id, (b) => M.putRows(b, page.rows)))
        if (page.next_after === null) break
        after = page.next_after
      }
      // the input tensors' cells (design 02 3.1)
      for (const t of full.tensors ?? []) {
        let from = ''
        for (;;) {
          if (!getState().books[id]) return
          const page = await tapi.tensorCells(t.id, from)
          setState((s) => M.withBook(s, id, (b) => M.putEntries(b, t.id, page.cells)))
          if (page.next === null) break
          from = page.next
        }
      }
      setState((s) => M.withBook(s, id, (b) => (b.loading ? { ...b, loading: false } : b)))
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setState((s) => M.removeWorkbook(s, id))
      } else if (e instanceof ApiError && e.status === 401) {
        this.signedOut('다시 로그인해 주세요.')
      } else {
        // read again the next time it is opened or the connection is back
        setState((s) => {
          const books = { ...s.books }
          delete books[id]
          return { ...s, books }
        })
        if (!(e instanceof NetworkError)) showToast(errorText(e))
        else setTimeout(() => this.open(getState().current), this.nextDelay())
      }
    }
  }

  async createWorkbook(name: string, visibility: Visibility): Promise<void> {
    const r = await tapi.createWorkbook(name, visibility)
    setState((s) => M.putWorkbook(s, r.workbook))
    this.open(r.workbook.id)
  }

  async updateWorkbook(id: Id, body: { name?: string; visibility?: Visibility; budget_alerts?: boolean }): Promise<void> {
    const r = await tapi.updateWorkbook(id, body)
    setState((s) => M.putWorkbook(s, r.workbook))
  }

  async deleteWorkbook(id: Id): Promise<void> {
    await tapi.deleteWorkbook(id)
    setState((s) => M.removeWorkbook(s, id))
  }

  // ---- items and budgets (sent at once) ----

  async addItem(workbookId: Id, role: BuiltinRole, name: string, parentId = 0): Promise<void> {
    const r = await tapi.addItem(workbookId, role, name, parentId)
    setState((s) => M.withBook(s, workbookId, (b) => M.putItem(b, r.item)))
  }

  // ---- dimensions people make (design 02 3.2): sent at once ----

  async addDim(workbookId: Id, body: TensorDimInput & { name: string; kind: TensorDim['kind'] }): Promise<TensorDim> {
    const r = await tapi.addDim(workbookId, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putDim(b, r.dim)))
    return r.dim
  }

  async updateDim(workbookId: Id, id: Id, body: TensorDimInput): Promise<void> {
    const r = await tapi.updateDim(id, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putDim(b, r.dim)))
    if (r.stale) showToast(STALE_TEXT)
  }

  async deleteDim(workbookId: Id, id: Id): Promise<void> {
    await tapi.deleteDim(id)
    setState((s) =>
      M.withBook(s, workbookId, (b) => {
        const d = b.dims[id]
        return d ? { ...b, dims: { ...b.dims, [id]: { ...d, deleted: true, version: d.version + 1 } } } : b
      }),
    )
  }

  async addDimItem(workbookId: Id, dimId: Id, name: string): Promise<void> {
    const r = await tapi.addDimItem(workbookId, dimId, name)
    setState((s) => M.withBook(s, workbookId, (b) => M.putItem(b, r.item)))
  }

  // ---- input tensors (design 02 3.1): sent at once, like budgets ----

  async addTensor(workbookId: Id, body: TensorDefInput & { name: string; dims: TensorDimKey[] }): Promise<TensorDef> {
    const r = await tapi.addTensor(workbookId, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putTensor(b, r.tensor)))
    return r.tensor
  }

  async updateTensor(workbookId: Id, id: Id, body: TensorDefInput): Promise<void> {
    const r = await tapi.updateTensor(id, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putTensor(b, r.tensor)))
    if (r.stale) showToast(STALE_TEXT)
  }

  async deleteTensor(workbookId: Id, id: Id): Promise<void> {
    await tapi.deleteTensor(id)
    setState((s) =>
      M.withBook(s, workbookId, (b) => {
        const t = b.tensors[id]
        return t ? M.putTensor(b, { ...t, deleted: true, version: t.version + 1 }) : b
      }),
    )
  }

  // ---- saved views (design 02 3.6): sent at once ----

  async addView(workbookId: Id, name: string, config: TensorViewConfig): Promise<TensorSavedView> {
    const r = await tapi.addView(workbookId, name, config)
    setState((s) => M.withBook(s, workbookId, (b) => M.putView(b, r.view)))
    return r.view
  }

  async updateView(workbookId: Id, id: Id, body: { name?: string; config?: TensorViewConfig; version?: number }): Promise<void> {
    const r = await tapi.updateView(id, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putView(b, r.view)))
    if (r.stale) showToast(STALE_TEXT)
  }

  async deleteView(workbookId: Id, id: Id): Promise<void> {
    await tapi.deleteView(id)
    setState((s) =>
      M.withBook(s, workbookId, (b) => {
        const v = b.views[id]
        return v ? M.putView(b, { ...v, deleted: true, version: v.version + 1 }) : b
      }),
    )
  }

  /** Cells of a tensor (5000 at most): the last write wins; the answer
      says when someone had changed one after what the screen showed. */
  async setTensorCells(workbookId: Id, id: Id, cells: TensorEntryInput[]): Promise<void> {
    const r = await tapi.setTensorCells(id, cells)
    setState((s) => M.withBook(s, workbookId, (b) => M.putEntries(b, id, r.cells)))
    if (r.stale) showToast(STALE_TEXT)
  }

  async updateItem(
    workbookId: Id,
    id: Id,
    body: { name?: string; parent_id?: number; position?: number; version?: number },
  ): Promise<void> {
    const r = await tapi.updateItem(id, body)
    setState((s) => M.withBook(s, workbookId, (b) => M.putItem(b, r.item)))
    if (r.stale) showToast(STALE_TEXT)
  }

  async deleteItem(workbookId: Id, id: Id): Promise<void> {
    await tapi.deleteItem(id)
    // the change (deleted, and a category's children) comes by the socket
    setState((s) =>
      M.withBook(s, workbookId, (b) => {
        const it = b.items[id]
        return it ? { ...b, items: { ...b.items, [id]: { ...it, deleted: true } } } : b
      }),
    )
  }

  async setBudget(workbookId: Id, month: string, categoryItem: Id, amount: number): Promise<void> {
    const r = await tapi.setBudget(workbookId, month, categoryItem, amount)
    setState((s) => M.withBook(s, workbookId, (b) => M.putBudget(b, r.budget)))
  }

  /** Copies the month before's budgets; returns how many came. */
  async copyBudgets(workbookId: Id, from: string, to: string): Promise<number> {
    const r = await tapi.copyBudgets(workbookId, from, to)
    setState((s) => M.withBook(s, workbookId, (b) => r.budgets.reduce((x, y) => M.putBudget(x, y), b)))
    return r.budgets.length
  }

  /** Rows read from a file: sent as they are (the server makes new items). */
  async addRows(workbookId: Id, rows: Parameters<typeof tapi.addRows>[1]): Promise<number> {
    const r = await tapi.addRows(workbookId, rows)
    setState((s) => M.withBook(s, workbookId, (b) => M.putRows(b, r.rows)))
    return r.created
  }

  // ---- rows: shown first, sent in order ----

  addRow(workbookId: Id, fields: M.RowFields): string {
    const clientId = newClientId()
    if (fields.payment_item) writeLocal(paymentKey(workbookId), String(fields.payment_item))
    this.queue({ key: newClientId(), type: 'add', workbookId, clientId, fields, status: 'sending' })
    return clientId
  }

  editRow(workbookId: Id, row: M.ShownRow, patch: M.RowPatch): void {
    // the version the screen showed; none when a change of it is still
    // queued (that one is mine, not someone else's)
    const queued = getState().ops.some((o) => o.clientId === row.client_id)
    this.queue({
      key: newClientId(),
      type: 'edit',
      workbookId,
      clientId: row.client_id,
      patch,
      version: queued ? 0 : row.version,
      status: 'sending',
    })
  }

  deleteRow(workbookId: Id, row: M.ShownRow): void {
    this.queue({ key: newClientId(), type: 'delete', workbookId, clientId: row.client_id, status: 'sending' })
  }

  private queue(op: M.RowOp): void {
    const waiting = getState().ops.some((o) => o.status === 'waiting')
    const o = waiting ? { ...op, status: 'waiting' as const } : op
    setState((s) => ({ ...s, ops: M.queueOp(s.ops, o) }))
    if (!waiting) void this.pumpOps()
  }

  private async pumpOps(): Promise<void> {
    if (this.opsBusy) return
    this.opsBusy = true
    try {
      for (;;) {
        const op = getState().ops.find((o) => o.status === 'sending')
        if (!op) break
        if (!(await this.sendOp(op))) break
      }
    } finally {
      this.opsBusy = false
    }
  }

  private done(op: M.RowOp, rows: TensorRow[]): void {
    setState((s) => {
      const next = { ...s, ops: s.ops.filter((o) => o.key !== op.key) }
      return rows.length ? M.withBook(next, op.workbookId, (b) => M.putRows(b, rows)) : next
    })
  }

  /** False when there is no connection (the changes wait). */
  private async sendOp(op: M.RowOp): Promise<boolean> {
    try {
      if (op.type === 'add') {
        const r = await tapi.addRow(op.workbookId, M.createBody(op.clientId, op.fields))
        this.done(op, [r.row])
      } else {
        // the row is named by client_id: its workbook must be read here
        let book = getState().books[op.workbookId]
        if (!book || book.loading) {
          await this.loadBook(op.workbookId)
          book = getState().books[op.workbookId]
          if (!getState().workbooks[op.workbookId]) {
            this.done(op, [])
            return true
          }
          if (!book || book.loading) throw new NetworkError('workbook not read')
        }
        const row = M.rowByClient(book, op.clientId)
        if (!row || row.deleted) {
          // gone meanwhile (deleted elsewhere), or never sent
          this.done(op, [])
          if (op.type === 'edit') showToast('이미 지워진 거래라 고치지 못했습니다.')
          return true
        }
        if (op.type === 'edit') {
          const body = M.patchBody(op.patch)
          if (op.version > 0) body.version = op.version
          const r = await tapi.updateRow(row.id, body)
          this.done(op, [r.row])
          if (r.stale) showToast(STALE_TEXT)
        } else {
          await tapi.deleteRow(row.id)
          this.done(op, [{ ...row, deleted: true, version: row.version + 1 }])
        }
      }
      this.opsDelay = OPS_RETRY_MIN_MS
      return true
    } catch (e) {
      if (e instanceof NetworkError) {
        setState((s) => ({
          ...s,
          ops: s.ops.map((o) => (o.status === 'sending' ? { ...o, status: 'waiting' as const } : o)),
        }))
        clearTimeout(this.opsRetry)
        this.opsRetry = setTimeout(() => this.flushOps(), this.opsDelay)
        this.opsDelay = Math.min(this.opsDelay * 2, OPS_RETRY_MAX_MS)
        return false
      }
      if (e instanceof ApiError && e.status === 401) {
        this.signedOut('다시 로그인해 주세요.')
        return false
      }
      this.done(op, [])
      showToast(e instanceof ApiError && e.status === 404 ? '이미 지워진 거래입니다.' : errorText(e))
      return true
    }
  }

  /** The connection is back: send the changes that waited. */
  flushOps(): void {
    clearTimeout(this.opsRetry)
    if (!getState().ops.some((o) => o.status === 'waiting')) return
    setState((s) => ({ ...s, ops: s.ops.map((o) => ({ ...o, status: 'sending' as const })) }))
    void this.pumpOps()
  }

  /** Waiting changes are kept in this browser, so that closing the app
      offline does not lose them. */
  private keepOps(): void {
    const s = getState()
    if (s.ops === this.keptOps || !s.me) return
    this.keptOps = s.ops
    writeLocal(opsKey(s.me.id), s.ops.length ? JSON.stringify(s.ops) : null)
  }

  private loadOps(me: Id): M.RowOp[] {
    try {
      const v = JSON.parse(readLocal(opsKey(me)) ?? '[]') as unknown
      return Array.isArray(v) ? (v as M.RowOp[]).filter((o) => o && typeof o.clientId === 'string') : []
    } catch {
      return []
    }
  }

  // ---- account ----

  async login(login: string, password: string, device: string): Promise<void> {
    await api.login(login, password, device)
    setState((s) => ({ ...s, phase: 'loading' }))
    await this.bootstrap()
  }

  async loginWithPasskey(device: string): Promise<void> {
    await loginWithPasskey(device)
    setState((s) => ({ ...s, phase: 'loading' }))
    await this.bootstrap()
  }

  async logout(): Promise<void> {
    const me = getState().me?.id
    try {
      await api.logout()
    } finally {
      this.signedOut(null)
      if (me) writeLocal(opsKey(me), null)
    }
  }

  signedOut(reason: string | null): void {
    const ws = this.ws
    this.ws = null
    ws?.close()
    clearInterval(this.pingTimer)
    clearTimeout(this.pongTimer)
    clearTimeout(this.reconnectTimer)
    clearTimeout(this.bootstrapTimer)
    clearTimeout(this.opsRetry)
    this.keptOps = null
    setState(() => ({ ...M.initialState, phase: 'signed-out', signedOutReason: reason, connection: 'offline' }))
  }
}

export const engine = new Engine()
